import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { patchJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import type { Invoice, InvoiceResponse } from '@/pages/InvoicesPage.types'

export function InvoiceDatesCard({
  invoice,
  allowEdit = true,
}: {
  invoice: Invoice
  allowEdit?: boolean
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const [dueOn, setDueOn] = React.useState(invoice.due_on)
  const updateDueDate = useMutation({
    mutationFn: (value: string) =>
      patchJSON<InvoiceResponse>(`/api/v1/invoices/${invoice.id}/due_date`, {
        invoice: { due_on: value },
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoice.id], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })

  React.useEffect(() => {
    setDueOn(invoice.due_on)
    setEditing(false)
  }, [invoice.due_on])

  return (
    <section className="space-y-3 px-4 py-3">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <h2 className="text-body font-semibold text-foreground">Dates</h2>
        {allowEdit && !editing && (
          <Button
            type="button"
            variant="outline"
            className="h-7 border-transparent px-2 text-caption"
            onClick={() => setEditing(true)}
          >
            Edit due date
          </Button>
        )}
      </div>
      <dl className="space-y-2 text-body-sm">
        <DetailPair label="Issued" value={displayDate(invoice.issue_on)} />
        {editing ? (
          <div className="space-y-2">
            <Label htmlFor="invoice-due-date" className="text-caption text-muted-foreground">
              Due date
            </Label>
            <Input
              id="invoice-due-date"
              type="date"
              className="h-8"
              value={dueOn}
              onChange={(inputEvent) => setDueOn(inputEvent.target.value)}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-8 px-3"
                disabled={updateDueDate.isPending || dueOn.length === 0}
                onClick={() => updateDueDate.mutate(dueOn)}
              >
                {updateDueDate.isPending ? 'Saving…' : 'Save due date'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-8 px-3"
                disabled={updateDueDate.isPending}
                onClick={() => {
                  setDueOn(invoice.due_on)
                  setEditing(false)
                }}
              >
                Cancel
              </Button>
            </div>
            {updateDueDate.isError && (
              <p className="text-caption text-destructive">Due date could not be saved.</p>
            )}
          </div>
        ) : (
          <DetailPair label="Due" value={displayDate(invoice.due_on)} />
        )}
      </dl>
    </section>
  )
}

function DetailPair({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  )
}
